import React, { useState } from 'react';
import { Navbar } from '../../components';
import './LexemeAI.css';

const LexemeAI = () => {
  const [playing, setPlaying] = useState(false);

  return (
    <div className="lexemeai-page">
      <Navbar />

      {/* HERO */}

      <section className="hero">
        <div className="hero-content">
          <h1>LexemeAI</h1>

          <h2>Turn Classroom Observations Into Instructional Intelligence</h2>

          <p className="hero-sub">
            An AI-powered platform that analyzes classroom observations,
            academic documents, discussions, and recorded sessions to generate
            structured instructional insights for school leadership and teacher development.
          </p>

          <div className="hero-buttons">
            <a
              href="https://apps.apple.com/app/id6755895604"
              target="_blank"
              rel="noreferrer"
              className="btn primary"
            >
              Download on App Store
            </a>

            <p className="trial-note">
              1-month free trial • Up to 4 sessions per day • Paid plans
              available on request • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* WHY LEXEMEAI */}

      <section className="section alt">
        <div className="container narrow">
          <h2>Why Schools Need Instructional Intelligence</h2>

          <p>
            School leaders observe classrooms, review reports, and analyze
            teaching practices across the academic year. Yet much of this
            valuable insight remains buried inside observation notes, PDFs,
            and informal discussions.
          </p>
          <p>
            LexemeAI helps schools organize and analyze classroom observations,
            teaching practices, instructional discussions, and academic evidence
            across the school year.
          </p>
          <p>
            Rather than treating classroom observations as isolated events,
            the platform identifies recurring instructional patterns over time —
            including participation trends, questioning strategies, classroom engagement,
            and instructional consistency across classrooms.
          </p>
          <p>
            Rather than treating classroom observations as isolated events,
            the platform identifies recurring instructional patterns over time —
            including participation trends, questioning strategies, classroom engagement,
            and instructional consistency across classrooms.
          </p>
        </div>
      </section>

      {/* WHO IT IS FOR */}

      <section className="section">
        <div className="container narrow">
          <h2>Who LexemeAI Is For</h2>

          <ul className="audience-list">
            <li>School Principals conducting classroom observations</li>
            <li>Academic Coordinators reviewing teaching practices</li>
            <li>School Leadership Teams preparing academic reviews</li>
            <li>Education Groups overseeing multiple campuses</li>
          </ul>
        </div>
      </section>

      {/* BENEFITS */}

      <section className="section">
        <div className="container">
          <h2>What School Leaders Gain</h2>

          <div className="card-grid">
            <div className="card">
              <h3>Instructional Visibility</h3>
              <p>
                Understand teaching patterns across classrooms and gain
                visibility into instructional practices across the school.
              </p>
            </div>

            <div className="card">
              <h3>Teacher Development Signals</h3>
              <p>
                Detect opportunities for mentoring, coaching, and professional
                development based on classroom evidence.
              </p>
            </div>

            <div className="card">
              <h3>Evidence-Based Leadership</h3>
              <p>
                Move beyond anecdotal observations to structured instructional
                intelligence that supports better academic decisions.
              </p>
            </div>

            <div className="card">
              <h3>Reduced Administrative Work</h3>
              <p>
                Transform hours of manual academic reporting into minutes of
                AI-assisted instructional analysis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}

      <section className="section alt">
        <div className="container">
          <h2>How LexemeAI Works</h2>

          <div className="steps">
            <div className="step">
              <span className="step-number">1</span>
              <h3>Capture Classroom Signals</h3>

              <p>
                Classroom observations, reports, PDFs, and audio or video
                recordings from classroom visits are uploaded into LexemeAI.
              </p>
            </div>

            <div className="step">
              <span className="step-number">2</span>
              <h3>AI Analyzes Teaching Patterns</h3>

              <p>
                LexemeAI detects instructional signals such as questioning
                patterns, participation levels, and engagement dynamics.
              </p>
            </div>

            <div className="step">
              <span className="step-number">3</span>
              <h3>Generate Instructional Intelligence</h3>

              <p>
                Insights are structured into outputs that help school leaders
                support teachers and improve classroom learning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* INSTRUCTIONAL INTELLIGENCE LOOP */}

      <section className="section">
        <div className="container">
          <h2>The Instructional Intelligence Loop</h2>

          <div className="card-grid">
            <div className="card">
              <h3>Observe Classrooms</h3>

              <p>
                Capture teaching practices through observation notes,
                recordings, and academic documents.
              </p>
            </div>

            <div className="card">
              <h3>AI Understands Teaching</h3>

              <p>
                LexemeAI identifies instructional signals such as engagement
                patterns and teacher-student interaction.
              </p>
            </div>

            <div className="card">
              <h3>Support Teacher Growth</h3>

              <p>
                Insights guide mentoring, coaching, and professional
                development for teachers.
              </p>
            </div>

            <div className="card">
              <h3>Improve Academic Outcomes</h3>

              <p>
                Schools gain system-level visibility into teaching quality
                and opportunities for instructional improvement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AI TEACHER DEVELOPMENT */}

      <section className="section alt">
        <div className="container narrow">
          <h2>AI-Assisted Teacher Development</h2>

          <p>
            LexemeAI also helps schools support teacher professional development
            through AI-assisted instructional coaching and guided learning experiences.
          </p>

          <p>
            Interactive 2D avatars guide teachers through training programs
            that incorporate school policies, curriculum requirements,
            instructional frameworks, and past classroom observations.
          </p>

          <p>
            School leaders remain in the loop, ensuring that AI supports
            teacher growth while preserving human judgment and mentorship.
          </p>
        </div>
      </section>

      {/* VIDEO DEMO */}

      <section className="section alt">
        <div className="container narrow">
          <h2>See LexemeAI in Action</h2>

          <div className="video-wrapper">
            <div className={`video-placeholder ${playing ? 'hide' : ''}`}>
              LexemeAI
            </div>

            <video
              className="demo-video"
              controls
              preload="metadata"
              onPlay={() => setPlaying(true)}
            >
              <source
                src="https://curvsort-profiles.s3.ap-south-1.amazonaws.com/public/LexemeAI_v1.1.m4v"
                type="video/mp4"
              />
              <track kind="captions" src="" label="English captions" />
            </video>
          </div>

          <p className="video-caption">Overview of LexemeAI</p>
        </div>
      </section>

      {/* PRODUCT COMMERCIALS */}

      <section className="section alt">
        <div className="container">
          <h2>LexemeAI in Real School Workflows</h2>

          <p className="section-intro">
            LexemeAI helps school leaders with classroom observations and also in
            targeted teachers development using instructional intelligence.
          </p>

          {/* VIDEO 1 */}

          <div className="demo-block">
            <h3>From Classroom Observation to Actionable Insights</h3>

            <p>
              School leaders conduct classroom observations every day.
              LexemeAI captures and analyzes these observations while keeping
              school leaders in the loop, transforming them into structured
              instructional intelligence.
            </p>

            <div className="video-wrapper">
              <video className="demo-video" controls preload="metadata">
                <source
                  src="https://curvsort-profiles.s3.ap-south-1.amazonaws.com/public/classroom_observations_withAudio.mp4"
                  type="video/mp4"
                />
                <track kind="captions" src="" label="English captions" />
              </video>
            </div>

            <p className="video-caption">
              Classroom observations → AI analysis → Instructional insights
            </p>
          </div>

          {/* VIDEO 2 */}

          <div className="demo-block">
            <h3>Connecting Teacher Training to Classroom Reality</h3>

            <p>
              Schools invest heavily in professional development, but applying
              training programs effectively in real classrooms remains
              difficult. LexemeAI connects teacher training frameworks with
              real classroom evidence to guide targeted teacher development.
            </p>

            <div className="video-wrapper">
              <video className="demo-video" controls preload="metadata">
                <source
                  src="https://curvsort-profiles.s3.ap-south-1.amazonaws.com/public/instructional_intelligence_withAudio.mp4"
                  type="video/mp4"
                />
                <track kind="captions" src="" label="English captions" />
              </video>
            </div>

            <p className="video-caption">
              Teacher training frameworks connected to real classroom evidence
            </p>
          </div>
        </div>
      </section>

      {/* PRIVACY */}

      <section className="section alt">
        <div className="container narrow">
          <h2>Designed With School Data Privacy in Mind</h2>

          <p>
            LexemeAI processes academic observations and documents securely.
            Uploaded data remains private to your institution.
          </p>
        </div>
      </section>

      {/* CTA */}

      <section className="cta">
        <h2>Start Your Free Trial</h2>

        <h3>Available on Mac, iPhone, and iPad</h3>

        <p>
          Start a 1-month free trial and explore how AI can transform classroom
          observations into instructional intelligence.
        </p>

        <a
          href="https://apps.apple.com/app/id6755895604"
          target="_blank"
          rel="noreferrer"
          className="btn primary"
        >
          Download on App Store
        </a>
      </section>
    </div>
  );
};

export default LexemeAI;
